/**
 * This store used to maintain quote related details in vuex store like current PageName,
 * network related details, loader timers etc. Respective getters, actions and mutations created
 * to manage these details
 */

const state = {
  showLoader: false,
  showAddressError: false,
  showUpdatedApp: false, // Shows newer version of app message
  showPasswordUpdateSuccess: false, // Shows password update success message
  showNetwork: true, // Default is that the network is fine
  showUpdateSuccess: false,
  showAddSuccess: false,
  showNetworkMessage: null,
  pageName: null,
  formPostData: null,
  loader_timer: null,
  delay_loading_icon: 3000,
  vehicleAccordion: null,
  driverAccordion: null,
  billPlan: null,
  homeBillPlan: null,
  showQuoteSummaryStatus: false,
  rc2LogoURL: '',
  startTime: null,
  pageActionName: '',
  inlineLoader: false,
  appointedCarrierStatus: false,
  isUserNavigation: false,
  rc2LogoURLWhite: '',
  productTypeId: null,
  clearRequiredError: false,
  agencyLogo: '',
  handle404: false,
  rc2HomeStatusError: false,
  rc2HomeStatusErrorData: '',
  rcHomeStatusError: false,
  rcHomeStatusErrorData: '',
  selectedAutoCarrier: '',
  selectedHomeCarrier: '',
  sameHomeBillingInfo: '',
};

const mutations = {
  /**
   * mutation to set agency logo if QRP is on a custom subdomain
   */
  setAgencyLogo(state, logo) {
    if (logo) {
      state.agencyLogo = logo;
    }
  },
  /**
   * mutation to set clear required error flag
   */
  setReqValidationFlagMutation(state, flag) {
    state.clearRequiredError = flag;
  },
  /**
   * Set user navigation status in store
   * @param {*} state
   * @param {*} navStatus
   */
  setUserNavFlagStatus(state, navStatus) {
    state.isUserNavigation = navStatus;
  },
  /**
   * set appointed carrier clicked status in store
   * @param {*} state
   * @param {*} status
   */
  setACStatusMutation(state, status) {
    state.appointedCarrierStatus = status;
  },
  /**
   * set inline loader mutation
   */
  setInlineLoaderMutation(state, status) {
    state.inlineLoader = status;
  },
  /**
   * Mutation to set start time
   * @param {*} state
   * @param {*} startTimeVal
   */
  setStartTimeMutation(state) {
    const startTimeVal = (new Date()).getTime();
    // console.log('start time mutation', startTimeVal);
    state.startTime = startTimeVal;
  },
  /**
   * set page action into state
   * @param {*} state
   * @param {*} pageAction
   */
  setPageActionNameMutation(state, pageAction) {
    state.pageActionName = pageAction;
  },
  /**
   * Mutation to set loader status. This will decide whether to display loader or not
   * @param {*} state vuex state
   * @param {*} status loader status true/false
   */
  setLoader(state, status) {
    state.showLoader = status;
  },
  setSimpleLoader(state, status) {
    state.showLoader = status;
  },
  /**
   * set billing plan
   * @param {} state vuex state
   * @param {*} plan billing plan selected by user
   */
  setBillingPlan(state, plan) {
    state.billPlan = plan;
  },
  /**
   * set billing plan of home for bundle
   * @param {} state vuex state
   * @param {*} plan billing plan selected by user
   */
  setHomeBillingPlan(state, plan) {
    state.homeBillPlan = plan;
  },
  /**
   * Set current displaying page name. This is used in formPost API call to send page name
   * in API request
   * @param {*} state vuex store
   * @param {*} pageName current page name from pageAPi response
   */
  setPageName(state, pageName) {
    state.pageName = pageName;
  },
  setAddressError(state, status) {
    state.showAddressError = status;
  },
  setUpdatedApp(state, status) {
    state.showUpdatedApp = status;
  },
  setPasswordUpdateSuccess(state, status) {
    state.showPasswordUpdateSuccess = status;

    // 750ms is slightly longer than Bootstrap's CSS `.fade` transition to 0 opacity
    setTimeout(() => { state.showPasswordUpdateSuccess = false; }, 5000);
  },
  setUpdateSuccessMsg(state, status) {
    state.showUpdateSuccess = status;

    // 750ms is slightly longer than Bootstrap's CSS `.fade` transition to 0 opacity
    setTimeout(() => { state.showUpdateSuccess = false; }, 5000);
  },
  setAddSuccessMsg(state, status) {
    state.showAddSuccess = status;

    // 750ms is slightly longer than Bootstrap's CSS `.fade` transition to 0 opacity
    setTimeout(() => { state.showAddSuccess = false; }, 5000);
  },
  /**
   * Set showNetwork state to true/false
   * @param {*} state vuex store
   * @param {*} status netowrk status true/false
   */
  setNetwork(state, status) {
    state.showNetwork = status;
  },
  /**
   * Set showNetWork message status in vuex store
   * @param {*} state vuex state
   * @param {*} status status true/false
   */
  setNetworkMessage(state, status) {
    state.showNetworkMessage = status;
  },
  /**
   * set quote summary status
   */
  setQuoteSummaryStatusToggle(state) {
    if (state.showQuoteSummaryStatus) {
      state.showQuoteSummaryStatus = false;
    } else {
      state.showQuoteSummaryStatus = true;
    }
  },
  setQuoteSummaryStatusReset(state) {
    state.showQuoteSummaryStatus = false;
  },
  setRc2LogoURL(state, status) {
    state.rc2LogoURL = status;
  },
  setRc2LogoURLWhite(state, status) {
    state.rc2LogoURLWhite = status;
  },
  setHandle404(state, status) {
    state.handle404 = status;
  },
  /**
   * set RC2 error status for home mutation
   */
  setMutRC2HomeError(state, status) {
    state.rc2HomeStatusError = status;
  },
  /**
   * set RC2 error description for home mutation
   */
  setMutRC2HomeErrorDesc(state, data) {
    state.rc2HomeStatusErrorData = data;
  },
  /* set RC error for home RC response */
  setRcHomeStatusError(state, status) {
    state.rcHomeStatusError = status;
  },
  setRcHomeStatusErrorData(state, status) {
    state.rcHomeStatusErrorData = status;
  },
  setSelectedAutoCarrier(state, data) {
    state.selectedAutoCarrier = data;
  },
  setSelectedHomeCarrier(state, data) {
    state.selectedHomeCarrier = data;
  },
  setSameHomeBillingInfo(state, data) {
    state.sameHomeBillingInfo = data;
  },
};

/* eslint no-shadow: ["error", { "allow": ["state"] }] */
const getters = {
  /**
   * get agency logo value
   */
  getAgencyLogo() {
    return state.agencyLogo;
  },
  /**
   * get clear required error flag value
   *
   */
  getClearRequiredErrorFlag() {
    return state.clearRequiredError;
  },
  getACStatus(state) {
    return state.appointedCarrierStatus;
  },
  /**
   * return inline loader status
   * @param {*} state
   */
  getInlineLoader(state) {
    return state.inlineLoader;
  },
  /**
   * Get quote summary status to show or hide based on clicks and current status
   * @param {*} state
   */
  getQuoteSummaryStatus(state) {
    return state.showQuoteSummaryStatus;
  },
  /**
   * Getters created to return current page name to pass in all formPost API
   * @param {*} state vuex state
   */
  getPageName(state) {
    return state.pageName;
  },
  /**
   * Getters created to return billing plan
   * @param {*} state vuex state
   */
  getBillingPlan(state) {
    return state.billPlan;
  },
  /**
   * Getters created to return billing plan of home for bundle
   * @param {*} state vuex state
   */
  getHomeBillingPlan(state) {
    return state.homeBillPlan;
  },
  getRc2LogoURL(state) {
    return state.rc2LogoURL;
  },
  getRc2LogoURLWhite(state) {
    return state.rc2LogoURLWhite;
  },
  /**
   * get start time from store
   */
  getStartTime(state) {
    return state.startTime;
  },
  /**
   * get page action from store
   */
  getPageAction(state) {
    return state.pageActionName;
  },
  /**
   * get handle 404 from store
   */
  getHandle404(state) {
    return state.handle404;
  },
  getRcHomeStatusError(state) {
    return state.rcHomeStatusError;
  },
  getRcHomeStatusErrorData(state) {
    return state.rcHomeStatusErrorData;
  },
  getSelectedAutoCarrier(state) {
    return state.selectedAutoCarrier;
  },
  getSelectedHomeCarrier(state) {
    return state.selectedHomeCarrier;
  },
  getSameHomeBillingInfo(state) {
    return state.sameHomeBillingInfo;
  },
};

const actions = {
  /**
   * set agency logo if QRP is on a custom subdomain
   */
  setAgencyLogo({ commit }) {
    const subDomain = window.location.hostname.split('.')[0];
    if (subDomain.includes('-qrp')) {
      commit('setAgencyLogo', subDomain);
    }
  },
  /**
   * set clear require validatoin flag true/false
   */
  setReqValidationFlag({ commit }, flag) {
    commit('setReqValidationFlagMutation', flag);
  },
  /**
   * set user navigation status if clicked link is search/dashboard/logout
   */
  setUserNavFlag({ commit }, navStatus) {
    commit('setUserNavFlagStatus', navStatus);
  },
  /**
   * set appointed carrier clicked status in store
   * @param {*} param0
   * @param {*} status
   */
  setAppointedCarrierStatus({ commit }, status) {
    commit('setACStatusMutation', status);
  },
  setInlineLoader({ commit }, status) {
    commit('setInlineLoaderMutation', status);
  },
  setStartTime({ commit }) {
    commit('setStartTimeMutation');
  },
  setPageActionName({ commit }, pageAction) {
    commit('setPageActionNameMutation', pageAction);
  },
  quoteSummaryStatusToggle({ commit }) {
    commit('setQuoteSummaryStatusToggle');
  },
  setRc2LogoURL({ commit }, logoURL) {
    commit('setRc2LogoURL', logoURL);
  },
  setRc2LogoURLWhite({ commit }, logoURL) {
    commit('setRc2LogoURLWhite', logoURL);
  },
  quoteSummaryStatusReset({ commit }) {
    commit('setQuoteSummaryStatusReset');
  },
  /**
   * action to set billing plan dispatched through selecting single radio
   * @param {*} param0
   * @param {*} config
   */
  setBillingPlan({ commit }, billingPlan) {
    commit('setBillingPlan', billingPlan);
  },
  /**
   * action to set billing plan dispatched through selecting single radio of hoem for bundle
   * @param {*} param0
   * @param {*} config
   */
  setHomeBillingPlan({ commit }, homeBillingPlan) {
    commit('setHomeBillingPlan', homeBillingPlan);
  },
  /**
   * Action to set loader status this action dispatched through axio-config js file
   * @param {*} config axios config params
   */
  setLoaderStatus({ commit }, config) {
    let loaderStartTime;
    let timerDelay = 0;// Default is no delay in showing loading UI
    let status = false; // Requests have status = true, responses have status = false

    /**
     * Scenarios:
     * 1) tab->tab: POST to endpoints `/navs`...
     * 2) page data: GET to endpoints `/pages`...
     * 3) user updates data: POST to endpoints `/rules`...
     */
    /**
     * make loader conditional as some event dont required loader
     */
    const matchExp = 'carriers/[0-9]+/';
    if (config.url && (config.url.includes('daily-activity/download') || config.url.match(matchExp))) {
      status = false;
    } else if (config.method) {
      status = true;

      // Check whether this is Scenario 3
      if (config.method === 'post' &&
        config.url &&
        config.url.indexOf('/rules') !== -1) {
        timerDelay = 3000; // Delay the loading UI so it doesn't show so quickly on user updates
      }
    }

    if (status === true && state.loader_timer === null) {
      loaderStartTime = Date.now(); // Set timestamp of loading beginning
      /* eslint func-names: ["error", "never"] */
      /* eslint prefer-arrow-callback:  */
      state.loader_timer = setInterval(function () {
        const currentTime = Date.now(); // Set timestamp of current interval
        if ((currentTime - loaderStartTime) > timerDelay) {
          commit('setLoader', status);
        }
      }, 100);
    } else {
      commit('setLoader', status); // Set loader status to false (done)
      clearInterval(state.loader_timer); // Stop timer since loading is done
      state.loader_timer = null; // Destroy timer
    }
  },
  setSimpleLoader({ commit }, status) {
    commit('setSimpleLoader', status);
  },
  /**
   * This action call setPageName mutation to set current page in vuex store.
   * This action dispatched from fetchData service
   * @param {*} status vuex state
   */
  setPageName({ commit }, status) {
    commit('setPageName', status);
  },
  setAddressError({ commit }, status) {
    commit('setAddressError', status);
  },
  setUpdatedApp({ commit }, status) {
    commit('setUpdatedApp', status);
  },
  setPasswordUpdateSuccess({ commit }, status) {
    commit('setPasswordUpdateSuccess', status);
  },
  setUpdateSuccessMsg({ commit }, status) {
    commit('setUpdateSuccessMsg', status);
  },
  setAddSuccessMsg({ commit }, status) {
    commit('setAddSuccessMsg', status);
  },
  /**
   * This action set network status and get called from App.Vue and axio-config.js
   * @param {*} status status true/false
   */
  setNetworkStatus({ commit }, status) {
    commit('setNetwork', status);
  },
  /**
   * This action set server message to display on network failure
   * Get called from axio-config.js
   * @param {*} status status true/false
   */
  setNetworkMessageStatus({ commit }, status) {
    let serverMessage = '';
    switch (status) {
      case 404:
        serverMessage = 'The server was not found. (Status code 404)';
        break;
      case 200:
        serverMessage = 'The server successfully returned a response. (Status code 200)';
        break;
      default:
        serverMessage = `System Error - Please Try Again: ${status}`;
        break;
    }

    commit('setNetworkMessage', serverMessage);
  },
  setHandle404({ commit }, status) {
    commit('setHandle404', status);
  },
  /**
   * set RC2 error status for home flow action
   * @param {*} param
   * @param {*} status
   */
  setRC2HomeError({ commit }, status) {
    commit('setMutRC2HomeError', status);
  },
  /**
   * set RC2 error description for home flow action
   * @param {*} param
   * @param {*} data
   */
  setRC2HomeErrorDesc({ commit }, data) {
    commit('setMutRC2HomeErrorDesc', data);
  },
  // Mutation for RC home error response
  setRCHomeError({ commit }, status) {
    commit('setRcHomeStatusError', status);
  },
  setRCHomeErrorDesc({ commit }, data) {
    commit('setRcHomeStatusErrorData', data);
  },
  setSelectedAutoCarrier({ commit }, data) {
    commit('setSelectedAutoCarrier', data);
  },
  setSelectedHomeCarrier({ commit }, data) {
    commit('setSelectedHomeCarrier', data);
  },
  setSameHomeBillingInfo({ commit }, data) {
    commit('setSameHomeBillingInfo', data);
  },
};

/**
 * export module to main store.js
 */
export default {
  state,
  mutations,
  actions,
  getters,
};



// WEBPACK FOOTER //
// ./src/store/modules/quote.js